import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import PasswordInput from '../components/PasswordInput';

function redirectAfterAuth(navigate, target = '/') {
  try { navigate(target, { replace: true }); } catch (e) {}
  setTimeout(() => {
    if (['/login', '/register'].includes(window.location.pathname)) {
      window.location.replace(target);
    }
  }, 400);
}

export default function Register() {
  const navigate = useNavigate();
  const { startEmailSignup, verifyOtp, completeLink, googleAuth, isAuthenticated } = useAuth();
  const [step, setStep] = useState('form');
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [existsProvider, setExistsProvider] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [confirmPassword, setConfirmPassword] = useState('');

  // Auto-redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) redirectAfterAuth(navigate, '/');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleGoogleSuccess = useCallback(async (credential) => {
    setGoogleLoading(true);
    try {
      const result = await googleAuth(credential, 'signup');

      if (result.status === 'LINK_REQUIRED') {
        setGoogleLoading(false);
        toast.info('An account exists. Sign in to link Google.');
        navigate('/login?linkGoogle=1');
        return;
      }

      if (result.status === 'CREATED' || result.status === 'LOGGED_IN') {
        toast.success(result.status === 'CREATED' ? 'Account created!' : 'Welcome back!');
        redirectAfterAuth(navigate, '/');
        return;
      }

      toast.warning(`Unexpected: ${result.status}`);
    } catch (err) {
      const data = err.response?.data;
      const status = err.response?.status;
      if (data?.error === 'INVALID_GOOGLE_AUDIENCE') {
        toast.error(`Config error: aud=${data.receivedAud}, expected=${JSON.stringify(data.allowedAudiences)}`);
      } else {
        toast.error(data?.detail ? `Google sign-up failed (${status}): ${data.detail}` : 'Google sign-up failed.');
      }
    } finally {
      setGoogleLoading(false);
    }
  }, [googleAuth, navigate]);

  const handleStartSignup = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) { toast.error('Fill in all fields'); return; }
    if (formData.password.length < 6) { toast.error('Password min 6 characters'); return; }
    if (formData.password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const r = await startEmailSignup(formData.email, formData.password, formData.name);
      if (r.status === 'OTP_SENT') { setStep('otp'); setResendCooldown(60); toast.success(`Code sent to ${formData.email}`); }
      else if (r.status === 'ACCOUNT_EXISTS') {
        setExistsProvider(r.provider);
        if (r.provider === 'google') { setStep('otp'); setResendCooldown(60); toast.info('Google account exists. Enter code to link.'); }
        else { toast.error('Account exists. Sign in instead.'); navigate('/login'); }
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return; setLoading(true);
    try { await startEmailSignup(formData.email, formData.password, formData.name); setResendCooldown(60); toast.success('New code sent'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const ctx = existsProvider === 'google' ? 'link' : 'signup';
      const r = await verifyOtp(formData.email, otp, ctx);
      if (r.status === 'VERIFIED' && r.next === 'LINK_EMAIL_TO_EXISTING') {
        const lr = await completeLink(formData.email, formData.password);
        if (lr.access_token) { toast.success('Linked!'); redirectAfterAuth(navigate, '/'); }
      } else if (r.access_token) {
        toast.success('Account verified!');
        redirectAfterAuth(navigate, '/');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Invalid code'); }
    finally { setLoading(false); }
  };

  // --- Google loading overlay ---
  if (googleLoading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-sm text-muted-foreground">Creating your account...</p>
        </div>
      </div>
    );
  }

  // --- OTP ---
  if (step === 'otp') {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md p-8 border-border bg-card" data-testid="otp-card">
          <div className="text-center mb-6">
            <h1 className="font-display text-2xl text-foreground font-semibold mb-2">Verify Your Email</h1>
            <p className="text-sm text-muted-foreground">Code sent to <strong className="text-foreground">{formData.email}</strong></p>
          </div>
          <form onSubmit={handleVerifyOtp} className="space-y-6">
            <div className="space-y-2">
              <Label>Verification Code</Label>
              <Input type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} required className="text-center text-3xl tracking-[0.5em] font-mono h-14" data-testid="otp-input" autoFocus />
            </div>
            <Button type="submit" className="w-full" disabled={loading || otp.length !== 6} data-testid="otp-submit-button">{loading ? 'Verifying...' : 'Verify & Create Account'}</Button>
          </form>
          <div className="mt-4 text-center">
            <p className="text-xs text-muted-foreground mb-2">Check inbox and spam folder.</p>
            <Button variant="ghost" size="sm" className="text-xs" disabled={resendCooldown > 0 || loading} onClick={handleResendOtp}>
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
            </Button>
          </div>
          <Button variant="ghost" className="w-full mt-2 text-xs" onClick={() => { setStep('form'); setOtp(''); }}>Back</Button>
        </Card>
      </div>
    );
  }

  // --- Form ---
  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md p-8 border-border bg-card" data-testid="register-card">
        <div className="text-center mb-6">
          <h1 className="font-display text-3xl text-foreground font-semibold mb-2">Join V watch</h1>
          <p className="text-muted-foreground text-sm">Create your account to start exploring</p>
        </div>
        <form onSubmit={handleStartSignup} className="space-y-4 mt-6" data-testid="register-form">
          <div className="space-y-2"><Label htmlFor="name">Full Name</Label><Input id="name" type="text" placeholder="John Doe" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required data-testid="register-name-input" /></div>
          <div className="space-y-2"><Label htmlFor="email">Email</Label><Input id="email" type="email" placeholder="your@email.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="register-email-input" /></div>
          <div className="space-y-2"><Label htmlFor="password">Password</Label><PasswordInput id="password" placeholder="Min 6 characters" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required minLength={6} data-testid="register-password-input" /></div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <PasswordInput id="confirmPassword" placeholder="Re-enter password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} data-testid="register-confirm-password-input" />
            {confirmPassword && formData.password !== confirmPassword && (
              <p className="text-xs text-red-400">Passwords do not match</p>
            )}
            {confirmPassword && formData.password === confirmPassword && confirmPassword.length >= 6 && (
              <p className="text-xs text-emerald-400">Passwords match</p>
            )}
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-button">{loading ? 'Sending code...' : 'Continue'}</Button>
        </form>
        <p className="text-sm text-muted-foreground text-center mt-6">Already have an account? <Link to="/login" className="text-primary hover:underline">Sign in</Link></p>
      </Card>
    </div>
  );
}
